import { useCountdown, useHasMounted } from 'hem-hooks';
import React from 'react';

type Props = {
  countdownTimer: Date;
};

const TabularNum = ({
  title,
  num,
  hideIfZero = false,
}: {
  title: string;
  num: number;
  hideIfZero?: boolean;
}) => {
  if (num === 0 && hideIfZero) {
    return null;
  }

  return (
    <div className="flex flex-col-reverse">
      <dt className="text-base font-bold text-[color:--hero-headline-color]">
        {title}
      </dt>
      <dd className="@3xl/hero:text-x2xl text-2xl tabular-nums text-[color:--hero-headline-color]">
        {num.toLocaleString(undefined, { minimumIntegerDigits: 2 })}
      </dd>
    </div>
  );
};

const Countdown: React.FC<Props> = ({ countdownTimer }): JSX.Element => {
  const hasMounted = useHasMounted();
  const [days, hours, minutes, seconds] = useCountdown(countdownTimer);

  // If the countdown is over, return null
  if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
    return null;
  }

  // If the countdown is not over, but the component has not mounted yet, return
  // empty html to prevent layout shift and also hydration failed error because
  // the initial UI does not match what was rendered on the server.
  if (!hasMounted) {
    return (
      <dl className="mt-2 flex gap-2 text-center opacity-0 md:mt-4 md:gap-4">
        {['Days', 'Hours', 'Minutes', 'Seconds'].map((title) => (
          <div key={title} className="flex flex-col-reverse">
            <dt className="text-base font-bold text-[color:--hero-headline-color]">
              {title}
            </dt>
            <dd className="@3xl/hero:text-x2xl text-2xl tabular-nums text-[color:--hero-headline-color]">
              00
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className="mt-2 flex animate-overlay-show gap-2 text-center md:mt-4 md:gap-4">
      <TabularNum title="Days" num={days} hideIfZero />
      <TabularNum title="Hours" num={hours} />
      <TabularNum title="Minutes" num={minutes} />
      <TabularNum title="Seconds" num={seconds} />
    </dl>
  );
};

export default Countdown;
